import { memo, useCallback, useMemo } from 'react';
import { SingularStationArea, StationSpec } from '@thive/fddm-types';

import { GapHorizontalIcon } from '@/assets/icons';
import { SelectInput } from '@/modules/common/components/inputs';
import { NumberInput } from '@/modules/common/components/inputs/components/NumberInput';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { NumUtils } from '@/modules/common/utils';
import { getCommonNumber, getCommonOption } from '@/modules/common/helpers/commonValue';
import {
  createEmptyAreas,
  pushAreaToItsCollection,
} from '@/modules/floorplanDesign/helpers/helpers';
import { DisabledReasonTooltip } from '../common/DisabledReasonTooltip';
import { useTranslatedOptions } from '@/modules/common/hooks';

const LOAD_HANDLING_DURATION_STRATEGY_IN_SIMULATION_OPTIONS: {
  labelKey: string;
  value: StationSpec['loadHandlingDurationStrategyInSimulation'];
}[] = [
  {
    labelKey: 'interface:properties.area.load_handling_duration_strategy_options.by_height',
    value: 'BY_HEIGHT',
  },
  {
    labelKey: 'interface:properties.area.load_handling_duration_strategy_options.by_override',
    value: 'BY_OVERRIDE',
  },
];

type Props = {
  selectedSingularStationAreas: readonly SingularStationArea[];
};

type AreaStationSpec = SingularStationArea['stationSpec'];

const canEditLoadDuration = (loadHandlingType: SingularStationArea['loadHandlingType']) =>
  loadHandlingType === 'INTAKE' ||
  loadHandlingType === 'STORAGE' ||
  loadHandlingType === 'OFF_VEHICLE_PROCESSING';

const canEditUnloadDuration = (loadHandlingType: SingularStationArea['loadHandlingType']) =>
  loadHandlingType === 'DELIVERY' ||
  loadHandlingType === 'STORAGE' ||
  loadHandlingType === 'OFF_VEHICLE_PROCESSING';

const canEditProcessingDuration = (loadHandlingType: SingularStationArea['loadHandlingType']) =>
  loadHandlingType === 'OFF_VEHICLE_PROCESSING' || loadHandlingType === 'ON_VEHICLE_PROCESSING';

const isByOverride = ({ stationSpec }: SingularStationArea): boolean =>
  stationSpec.loadHandlingDurationStrategyInSimulation === 'BY_OVERRIDE';

/** @description Load time applies to 'By override' areas whose load handling type loads. */
const hasLoadDuration = (area: SingularStationArea): boolean =>
  isByOverride(area) && canEditLoadDuration(area.loadHandlingType);

/** @description Unload time applies to 'By override' areas whose load handling type unloads. */
const hasUnloadDuration = (area: SingularStationArea): boolean =>
  isByOverride(area) && canEditUnloadDuration(area.loadHandlingType);

/** @description Operation time applies to processing areas, whatever the duration strategy. */
const hasProcessingDuration = ({ loadHandlingType }: SingularStationArea): boolean =>
  canEditProcessingDuration(loadHandlingType);

function SingularStationSpecPropertiesComponent({ selectedSingularStationAreas }: Props) {
  const loadHandlingDurationStrategyInSimulationOptions = useTranslatedOptions(
    LOAD_HANDLING_DURATION_STRATEGY_IN_SIMULATION_OPTIONS,
  );
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  // null means the selected areas have different values ('Mixed')
  const commonValues = useMemo(() => {
    const specs = selectedSingularStationAreas.map(({ stationSpec }) => stationSpec);

    return {
      zMm: getCommonNumber(specs.map(({ z }) => NumUtils.metersToMmRounded(z))),
      strategy: getCommonOption(specs.map((spec) => spec.loadHandlingDurationStrategyInSimulation)),
    };
  }, [selectedSingularStationAreas]);

  // Load time shows when any selected area uses it; only editable when all of them do
  const loadDurationAreas = useMemo(
    () => selectedSingularStationAreas.filter(hasLoadDuration),
    [selectedSingularStationAreas],
  );
  const isLoadDurationPartialSelection =
    loadDurationAreas.length !== selectedSingularStationAreas.length;
  // null shows 'Mixed'
  const loadDuration = useMemo(
    () =>
      getCommonNumber(
        loadDurationAreas.map(
          ({ stationSpec }) => stationSpec.loadDurationOverrideInSimulation ?? 0,
        ),
      ),
    [loadDurationAreas],
  );

  // Unload time shows when any selected area uses it; only editable when all of them do
  const unloadDurationAreas = useMemo(
    () => selectedSingularStationAreas.filter(hasUnloadDuration),
    [selectedSingularStationAreas],
  );
  const isUnloadDurationPartialSelection =
    unloadDurationAreas.length !== selectedSingularStationAreas.length;
  // null shows 'Mixed'
  const unloadDuration = useMemo(
    () =>
      getCommonNumber(
        unloadDurationAreas.map(
          ({ stationSpec }) => stationSpec.unloadDurationOverrideInSimulation ?? 0,
        ),
      ),
    [unloadDurationAreas],
  );

  // Operation time shows when any selected area uses it; only editable when all of them do
  const processingDurationAreas = useMemo(
    () => selectedSingularStationAreas.filter(hasProcessingDuration),
    [selectedSingularStationAreas],
  );
  const isProcessingDurationPartialSelection =
    processingDurationAreas.length !== selectedSingularStationAreas.length;
  // null shows 'Mixed'
  const processingDuration = useMemo(
    () =>
      getCommonNumber(
        processingDurationAreas.map(
          ({ stationSpec }) => stationSpec.processingExtension?.processingDurationInSimulation ?? 0,
        ),
      ),
    [processingDurationAreas],
  );

  /** Applies the same station spec change to every selected area in one update (one undo step). */
  const updateStationSpecs = useCallback(
    (updateSpec: (spec: AreaStationSpec) => AreaStationSpec) => {
      const updatedAreas = createEmptyAreas();
      selectedSingularStationAreas.forEach((area) => {
        pushAreaToItsCollection(updatedAreas, {
          ...area,
          stationSpec: updateSpec(area.stationSpec),
        });
      });
      fddmSvc.updateAreas(updatedAreas);
    },
    [fddmSvc, selectedSingularStationAreas],
  );

  const onZChange = useCallback(
    (newValueMm: number) => {
      if (Number.isNaN(newValueMm)) return;

      const newValue = NumUtils.mmToMeters(newValueMm);

      fddmSvc.setPreference('singularStationArea.stationSpec.z', newValue);
      updateStationSpecs((spec) => ({ ...spec, z: newValue }));
    },
    [fddmSvc, updateStationSpecs],
  );

  const onStrategyChange = useCallback(
    (newValue: StationSpec['loadHandlingDurationStrategyInSimulation']) => {
      fddmSvc.setPreference(
        'singularStationArea.stationSpec.loadHandlingDurationStrategyInSimulation',
        newValue,
      );
      updateStationSpecs((spec) => ({
        ...spec,
        loadHandlingDurationStrategyInSimulation: newValue,
      }));
    },
    [fddmSvc, updateStationSpecs],
  );

  const onLoadDurationChange = useCallback(
    (newValue: number) => {
      if (isLoadDurationPartialSelection || Number.isNaN(newValue)) return;

      fddmSvc.setPreference(
        'singularStationArea.stationSpec.loadDurationOverrideInSimulation',
        newValue,
      );
      updateStationSpecs((spec) => ({ ...spec, loadDurationOverrideInSimulation: newValue }));
    },
    [fddmSvc, isLoadDurationPartialSelection, updateStationSpecs],
  );

  const onUnloadDurationChange = useCallback(
    (newValue: number) => {
      if (isUnloadDurationPartialSelection || Number.isNaN(newValue)) return;

      fddmSvc.setPreference(
        'singularStationArea.stationSpec.unloadDurationOverrideInSimulation',
        newValue,
      );
      updateStationSpecs((spec) => ({ ...spec, unloadDurationOverrideInSimulation: newValue }));
    },
    [fddmSvc, isUnloadDurationPartialSelection, updateStationSpecs],
  );

  const onProcessingDurationChange = useCallback(
    (newValue: number) => {
      if (isProcessingDurationPartialSelection || Number.isNaN(newValue)) return;

      fddmSvc.setPreference(
        'singularStationArea.stationSpec.processingExtension.processingDurationInSimulation',
        newValue,
      );
      updateStationSpecs((spec) => ({
        ...spec,
        processingExtension: {
          ...spec.processingExtension,
          processingDurationInSimulation: newValue,
        },
      }));
    },
    [fddmSvc, isProcessingDurationPartialSelection, updateStationSpecs],
  );

  if (selectedSingularStationAreas.length === 0) return null;

  return (
    <>
      <PropertiesLabel i18nkey='interface:properties.area.load_elevation'>
        <NumberInput
          value={commonValues.zMm}
          unit='mm'
          prefix={<GapHorizontalIcon />}
          sx={{ width: '100%' }}
          onChange={onZChange}
        />
      </PropertiesLabel>

      <PropertiesLabel i18nkey='interface:properties.area.load_duration_strategy'>
        <SelectInput
          options={loadHandlingDurationStrategyInSimulationOptions}
          onChange={onStrategyChange}
          value={commonValues.strategy}
          showMixed
        />
      </PropertiesLabel>

      {loadDurationAreas.length > 0 && (
        <PropertiesLabel i18nkey='interface:properties.area.load_duration'>
          <DisabledReasonTooltip
            reasonKey={
              isLoadDurationPartialSelection
                ? 'interface:properties.area.partial_selection.singular_load_duration'
                : undefined
            }
          >
            <NumberInput
              // undefined (not false) keeps NumberInput's own read-only-mode check
              disabled={isLoadDurationPartialSelection ? true : undefined}
              value={loadDuration}
              unit='sec'
              prefix={<GapHorizontalIcon />}
              sx={{ width: '100%' }}
              minValue={0}
              onChange={onLoadDurationChange}
            />
          </DisabledReasonTooltip>
        </PropertiesLabel>
      )}

      {unloadDurationAreas.length > 0 && (
        <PropertiesLabel i18nkey='interface:properties.area.unload_duration'>
          <DisabledReasonTooltip
            reasonKey={
              isUnloadDurationPartialSelection
                ? 'interface:properties.area.partial_selection.singular_unload_duration'
                : undefined
            }
          >
            <NumberInput
              disabled={isUnloadDurationPartialSelection ? true : undefined}
              value={unloadDuration}
              unit='sec'
              prefix={<GapHorizontalIcon />}
              sx={{ width: '100%' }}
              minValue={0}
              onChange={onUnloadDurationChange}
            />
          </DisabledReasonTooltip>
        </PropertiesLabel>
      )}

      {processingDurationAreas.length > 0 && (
        <PropertiesLabel i18nkey='interface:properties.area.operation_time'>
          <DisabledReasonTooltip
            reasonKey={
              isProcessingDurationPartialSelection
                ? 'interface:properties.area.partial_selection.processing_duration'
                : undefined
            }
          >
            <NumberInput
              disabled={isProcessingDurationPartialSelection ? true : undefined}
              value={processingDuration}
              unit='sec'
              prefix={<GapHorizontalIcon />}
              sx={{ width: '100%' }}
              minValue={0}
              onChange={onProcessingDurationChange}
            />
          </DisabledReasonTooltip>
        </PropertiesLabel>
      )}
    </>
  );
}

export const StationSpecProperties = memo(SingularStationSpecPropertiesComponent);
